import { gql, ObservableQuery } from '@apollo/client'
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { CarouselJsonLd, FAQPageJsonLd, NextSeo, WebPageJsonLd } from 'next-seo'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import getApolloClient, { FAKE_CTX } from 'src/apollo-client'
import { DIRECTORY_GUIDE_LOCATIONS } from 'src/components/DirectoryFacility/directoryQuery'
import CrossLinkLogistics from 'src/components/PersonalizedLanding/CrossLinkLogistics'
import CrossLinkOnDemand from 'src/components/PersonalizedLanding/CrossLinkOnDemand'
import PillarDisambiguation from 'src/components/PillarDisambiguation'
import { SpaceType } from 'src/components/SearchBar/types'
import SearchManager from 'src/components/SearchManager'
import {
  NEARBY_SEARCH_COUNTS,
  NearbySearchCountsData,
  NearbySearchCountsResult,
  NearbySearchCountsVars
} from 'src/components/SearchManager/hostCtaQuery'
import SeoFooter, {
  FAQ_VISIBLE_COUNT
} from 'src/components/SearchManager/SeoFooter'
import { SSG_PAGE_SIZE } from 'src/constants'
import { getStartingPrice } from 'src/constants/cityPriceFloors'
import { parseCitySeo } from 'src/constants/citySeoContent'
import { CARD_LOCATION_FIELDS } from 'src/constants/fragments'
import { bandForPillar, captionForPillar } from 'src/constants/logoBands'
import {
  getPillarOgImage,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_WIDTH
} from 'src/constants/ogImages'
import { PLACE_ID_TO_COORDS } from 'src/constants/placeIdCoords'
import {
  HOOD_MAP,
  METRO_PARENT_MAP,
  PILLAR_NS_MAP,
  PILLAR_PAGE_MAP,
  PillarPage,
  SEARCH_PAGE_CITY_MAP,
  SearchHood,
  SearchPageCity
} from 'src/constants/searchPages'
import FilterProvider from 'src/context/FilterProvider'
import { useTrackLastSearch } from 'src/hooks/useLastSearch'
import Logos from 'src/screens/Landing/Logos'
import {
  ILocation,
  IQuery,
  IQueryDirectoryLocationsArgs,
  IQueryGooglePlaceDetailsArgs,
  IQuerySearchLocationsArgs
} from 'src/types/graphql'
import { IQueryParams } from 'src/types/types'
import {
  buildDirectoryFichaHref,
  DIRECTORY_CATEGORY_LOCATION_TYPES,
  directoryCategoryForPillar
} from 'src/utils/directory'
import { directoryGuideMode } from 'src/utils/directoryGuide'
import { processRequestError } from 'src/utils/errors'
import { loadTranslations } from 'src/utils/i18nServerProps'
import { toOgLocale } from 'src/utils/seo'

// Login popup runs entirely client-side (Firebase auth, browser-only APIs).
// `{ ssr: false }` keeps it off the SSR tree so initial SSR/CSR trees match.
const GooglePopup = dynamic(() => import('src/components/Login/GooglePopup'), {
  ssr: false
})

// Stale-listing salvage toast — fires when arriving here via the deactivated-
// listing redirect carrying `?lost=1[&lostId=...]`. Also pings Meta with an
// out-of-stock signal so DPA creatives stop serving the dead listing.
const LostListingToast = dynamic(
  () => import('src/components/LostListingToast'),
  { ssr: false }
)

const SEARCH_LOCATIONS = gql`
  ${CARD_LOCATION_FIELDS}
  query SearchLocationsBE(
    $pagination: PaginationInput!
    $searchInput: SearchLocationsInput!
    $placeId: String!
    $fetchPlace: Boolean!
  ) {
    searchLocations(pagination: $pagination, searchInput: $searchInput) {
      total
      hits {
        ...CardLocationFields
      }
    }
    googlePlaceDetails(placeId: $placeId) @include(if: $fetchPlace) {
      placeId
      lat
      lng
    }
  }
`

const urlF = (
  pillar: PillarPage,
  city: SearchPageCity | null,
  hood: SearchHood | null
) =>
  `https://www.spotme.mx/${pillar}${city ? (hood ? `/${city}/${hood}` : `/${city}`) : ''}`

type IProps = {
  pillar: PillarPage
  city: SearchPageCity | null
  hood: SearchHood | null
  label: string
  searchName: string
  cityName: string | null
  cityNameShort: string | null
  hoodName: string | null
  initialFilters: Partial<IQueryParams>
  initialData: ObservableQuery.Result<
    Pick<IQuery, 'searchLocations' | 'googlePlaceDetails'>
  >
  locale: string
  keywords: string[]
  nearbySearchData: NearbySearchCountsResult | null
  /** Top directory rows, SSR-fetched ONLY when the city has zero SpotMe supply (2026-08-28). */
  initialDirectory: ILocation[] | null
  buildDate: string
}

const Page: NextPage<IProps> = ({
  pillar,
  city,
  hood,
  searchName,
  cityName,
  cityNameShort,
  hoodName,
  initialFilters,
  initialData,
  locale,
  keywords,
  nearbySearchData,
  initialDirectory,
  buildDate
}) => {
  const { t } = useTranslation(PILLAR_NS_MAP[pillar])
  const { t: tStats } = useTranslation('platform_stats')
  const { t: tCommon } = useTranslation('common')

  // Persist this visit as the renter's last search so the home-page resume
  // card can offer a one-tap return on the next session.
  useTrackLastSearch({ pillar, city, hood })

  if (!initialData) return null

  const locationName = hoodName ?? cityNameShort ?? cityName ?? 'México'
  const { searchNameH1 } = PILLAR_PAGE_MAP[pillar]
  // The city's own floor when it sits above the national one — a national
  // "Desde $500" over a $1,150 cheapest card is a false price (2026-10-14).
  // null = no listings of this kind in the market: no price claim at all.
  // A hood page makes no claim: the city floor can sit far from the hood
  // (Centro parking's cheapest is $1,950 under a $1,150 city floor).
  const startingPrice = hood ? null : getStartingPrice(pillar, city)
  const totalResults = initialData.data?.searchLocations?.total
  // Known-empty market (SSR'd 0), as opposed to unknown (SSG / no data).
  const isZeroSupply = typeof totalResults === 'number' && totalResults === 0
  const citySeoRaw = city
    ? t(`CITY_SEO.${city}` as never, {
        returnObjects: true,
        defaultValue: null
      })
    : null
  const metroParent = city ? METRO_PARENT_MAP[city] : undefined
  const parentSeoRaw = metroParent
    ? t(`CITY_SEO.${metroParent}` as never, {
        returnObjects: true,
        defaultValue: null
      })
    : undefined
  const citySeo = parseCitySeo(citySeoRaw, city, hood, parentSeoRaw)
  // Satellite municipalities and hoods search metro-wide (zoom 11 around the
  // place), so `totalResults` counts the whole metro, not this place — Santa
  // Catarina showed "28 espacios" with zero of its own (2026-10-01 audit).
  // Never pin that count on the place name.
  const isNearbyScoped = !!hood || !!metroParent

  const baseSearchName = searchNameH1 ?? searchName
  const title =
    citySeo?.TITLE ?? `${baseSearchName} en ${locationName} | SpotMe`
  const description =
    citySeo?.DESCRIPTION ??
    (isNearbyScoped
      ? `✓ ${searchName} en ${locationName} y alrededores. Compara precios, tamaños y fotos, y reserva en línea en SpotMe.`
      : (totalResults ?? 0) > 0
        ? `✓ ${totalResults}+ ${searchName.toLowerCase()} en ${locationName}. Compara precios, tamaños y fotos, y reserva en línea en SpotMe.`
        : `✓ ${searchName} en ${locationName}. Compara precios, tamaños y fotos, y reserva en línea en SpotMe.`)
  const url = urlF(pillar, city, hood)
  // Per-pillar social card — a parking or naves search page must not unfurl
  // with the self-storage boxes shot.
  const image = getPillarOgImage(pillar)

  const breadcrumbs = [
    { href: '/', text: 'SpotMe' },
    { href: `/${pillar}`, text: searchName },
    ...(city
      ? [{ href: `/${pillar}/${city}`, text: cityNameShort ?? cityName }]
      : []),
    ...(hood ? [{ href: `/${pillar}/${city}/${hood}`, text: hoodName }] : [])
  ]

  const locationLabel = hoodName || cityName || 'cerca de ti'

  // Cross-pillar disambiguation uses the city slug only (hood is dropped) —
  // Warehouse is NOT in PRERENDER_HOOD_PILLARS, so a hood-scoped link from a
  // mini-bodegas hood page would land on an SSG'd-but-warehouse-empty route
  // or 404. City-level cross-links are safe in both directions.
  const disambiguationCity = city ?? undefined

  const header = (
    <>
      <div className="border-b border-ink-100/15 bg-white-500 px-4 pt-3 pb-5 md:px-6 md:pt-4 md:pb-4">
        {/* Breadcrumbs — editorial small-caps */}
        <nav
          aria-label="breadcrumb"
          className="mb-4 flex flex-wrap items-center gap-1.5 text-[11px] tracking-[0.08em] text-ink-200 uppercase"
        >
          {breadcrumbs.map(({ href, text }, i) => {
            const isLast = i === breadcrumbs.length - 1
            return (
              <span key={href} className="flex items-center gap-1.5">
                {i > 0 && (
                  <span aria-hidden="true" className="text-ink-100/60">
                    /
                  </span>
                )}
                {/* No viewport prefetch: on a mobile landing these links
                    pulled the home + pillar-root route chunks and this very
                    page's own data JSON over Slow 4G, competing with the
                    LCP image and the first tap (2026-09-25 trace). A tap
                    still prefetches on touchstart. Same policy as Footer. */}
                <Link
                  prefetch={false}
                  href={href}
                  className={`transition-colors ${
                    isLast
                      ? 'font-semibold text-ink-700'
                      : 'hover:text-blue-500'
                  }`}
                >
                  {text}
                </Link>
              </span>
            )
          })}
        </nav>

        {/* Editorial H1 + inline meta — Airbnb-style information density */}
        {/* Each visible text run is ONE expression (template literal), not
            text+{' '}+text or static+{expr}. React inserts `<!-- -->` boundary
            comments only between ADJACENT text nodes; comment-stripping
            browsers/proxies (FB IAB, content blockers, some Cloudflare paths)
            remove them → React 19 #418. A single text node abutting the
            <span> element needs no delimiter comment. Confirmed firing as
            `…en renta<!-- --> <span>en <!-- -->Ciudad de México` on pillar
            city/hood pages (Datadog 2026-06-12). See CLAUDE.md "Trigger H". */}
        <h1 className="max-w-3xl text-[26px] leading-[1.1] font-medium tracking-[-0.01em] text-ink-700 md:text-[28px]">
          {citySeo?.H1 ?? (
            <>
              {`${searchNameH1 ?? searchName} `}
              <span className="font-display text-[0.95em] font-normal text-blue-500 italic">
                {`en ${locationLabel}`}
              </span>
            </>
          )}
        </h1>

        {/* Inline meta strip — count, price-from, verification.
            On a ZERO-supply market (`totalResults === 0`, known at SSR) the
            local claims come off: "Desde $X/mes" would price a market with
            no listings and "Anfitriones verificados" would sit directly above
            the unverified-directory block that now renders there
            (2026-08-28). The platform-wide rating item was removed
            2026-09-30 — there are no real reviews yet. An unknown count (SSG,
            no data) keeps the full strip. */}
        <dl className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[13px] text-ink-200 md:gap-x-5">
          {typeof totalResults === 'number' && totalResults > 0 ? (
            <div className="flex items-center gap-1.5">
              <dt className="sr-only">
                {tCommon('SEARCH_MANAGER.META_AVAILABLE_SPACES')}
              </dt>
              <dd>
                <span className="font-semibold text-ink-700">
                  {totalResults}
                </span>
                {isNearbyScoped
                  ? ` ${tCommon('SEARCH_MANAGER.META_SPACES_NEARBY')}`
                  : ' espacios'}
              </dd>
            </div>
          ) : null}
          {!isZeroSupply && startingPrice !== null && (
            <>
              <span
                aria-hidden="true"
                className="hidden h-3 w-px bg-ink-100/40 md:inline-block"
              />
              <div className="flex items-center gap-1.5">
                <dt className="sr-only">
                  {tCommon('SEARCH_MANAGER.META_PRICE_FROM')}
                </dt>
                <dd>
                  {'Desde '}
                  <span className="font-semibold text-ink-700">
                    {startingPrice}
                  </span>
                  {'/mes'}
                </dd>
              </div>
            </>
          )}
          {!isZeroSupply && (
            <>
              <span
                aria-hidden="true"
                className="hidden h-3 w-px bg-ink-100/40 md:inline-block"
              />
              {/* Plain meta, like its neighbours (2026-09-26): as blue-500
                  uppercase it read as a link and drew taps. The blue lives on
                  the glyph only. Wording is a verification claim — restyle
                  freely, reword only with legal. */}
              <div className="flex items-center gap-1.5">
                <dt className="sr-only">
                  {tCommon('SEARCH_MANAGER.META_VERIFICATION')}
                </dt>
                <dd className="flex items-center gap-1">
                  <i
                    aria-hidden="true"
                    className="si si-circle-check text-[13px] text-blue-500"
                  />
                  {tCommon('SEARCH_MANAGER.META_VERIFIED_HOSTS')}
                </dd>
              </div>
            </>
          )}
        </dl>
      </div>
      {/* Cross-pillar disambiguation — renders only on SelfStorage and
          Warehouse city/hood pages where "renta de bodegas" intent is
          genuinely ambiguous. Internally pillar-gated; null for Parking
          and Industrial. City context preserved; hood intentionally
          dropped because Warehouse has no SSG'd hood routes. */}
      <PillarDisambiguation
        pillar={pillar}
        currentCity={disambiguationCity}
        compact
      />
    </>
  )

  // The cross-link is composed into the `seoFooter` slot (rather than added as
  // a separate prop on SearchManager) because it must render in BOTH the
  // mobile drawer and desktop right-rail surfaces — and `SearchManager`
  // already renders `seoFooter` in both spots. Using the most-specific slug
  // available (hood → city) so a CDMX hood like `polanco` still resolves to
  // the on-demand market via `resolveOnDemandCity`.
  const onDemandSlug = hood ?? city ?? undefined
  const seoFooter = (
    <>
      <CrossLinkOnDemand pillar={pillar} currentCity={onDemandSlug} />
      {/* Soluciones Logísticas (3PL) cross-link — nationwide, always
          visible; internally gated to Warehouse/Industrial pillars only
          (no-ops on SelfStorage/Parking, where CrossLinkOnDemand above
          already owns the equivalent slot). */}
      <CrossLinkLogistics pillar={pillar} />
      {/* Renter trust strip. These search pages are the site's single biggest
          entry surface — ~10.3k entry sessions/30d across city + hood vs ~2.9k
          for the home and pillar landings combined — and 53% of them arrive on
          Organic Search, i.e. from a query, having never seen a SpotMe ad or
          the homepage. That is the coldest traffic we get, and until now it was
          the one renter surface with no company proof on it at all.

          It sits at the END of the SEO stack on purpose: the listings ARE the
          proof on a results page, so the band reinforces below them rather than
          displacing them. Do not move it above the results. */}
      <Logos
        band={bandForPillar(pillar)}
        caption={captionForPillar(pillar)}
        className="border-t border-ink-100/15 bg-grey-250 py-10 md:bg-cream-300"
      />
      <SeoFooter pillar={pillar} city={city} hood={hood} citySeo={citySeo} />
    </>
  )

  const ownPrefix = `/${pillar}/${city}/`
  const ownHits = (initialData.data?.searchLocations?.hits ?? [])
    // The search-matched config's route — a mixed parking+storage location's
    // location-level route points at its parking config and fell out of the
    // storage ItemList (2026-10-01).
    .map((hit) => hit.defaultConfiguration?.urlRoute ?? hit.urlRoute)
    .filter((route): route is string => !!route?.startsWith(ownPrefix))
  const itemListUrls =
    ownHits.length > 0
      ? ownHits
      : (initialDirectory ?? [])
          .map(buildDirectoryFichaHref)
          .filter((href): href is string => !!href)

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={url}
        // NEVER noindex a zero-supply page (2026-10-04). A 09-30 rule
        // noindexed "confirmed empty" pages (0 results + 0 directory rows);
        // Ahrefs' 10-02 crawl found 466 of them still in the sitemap, and in
        // GSC Jun 30–Sep 28 they had earned 2,896 clicks / 65k impressions
        // (~16% of organic clicks — Xalapa, Morelia, Reynosa, Ecatepec…).
        // Empty of OUR supply is not thin to the searcher. Re-propose only
        // with per-URL GSC evidence AND the sitemap excluding the same set.
        openGraph={{
          type: 'website',
          url,
          title,
          description,
          locale: toOgLocale(locale),
          images: [
            {
              url: image,
              width: OG_IMAGE_WIDTH,
              height: OG_IMAGE_HEIGHT,
              alt: t('OG_IMAGE_ALT')
            }
          ]
        }}
        additionalMetaTags={[
          {
            name: 'keywords',
            content:
              keywords
                .map((k) => k + ` (${hoodName ?? cityNameShort ?? cityName})`)
                .join(', ') ?? ''
          }
        ]}
      />
      <WebPageJsonLd
        description={description}
        id={url + '#webpage'}
        lastReviewed={buildDate}
        reviewedBy={{ '@type': 'Organization', name: 'SpotMe' }}
        breadcrumb={{
          '@type': 'BreadcrumbList',
          itemListElement: breadcrumbs.map(({ href, text }, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: text,
            item: `https://www.spotme.mx${href}`
          }))
        }}
      />
      {/* No LocalBusiness here (2026-09-30): a search page is not a storage
          facility at this address, and its old `rating` was hardcoded per
          city. Rating markup only ever from genuine stored reviews. */}
      <FAQPageJsonLd
        mainEntity={
          citySeo?.FAQS
            ? citySeo.FAQS.map(({ question, answer }) => ({
                questionName: question,
                acceptedAnswerText: answer
              }))
            : t('FAQS.ITEMS', {
                returnObjects: true,
                cities: tStats('CITIES')
              })
                // Same cap as the visible FAQ in SeoFooter — markup must
                // never carry questions the page doesn't show.
                .slice(0, FAQ_VISIBLE_COUNT)
                .map(
                  ({
                    title,
                    description
                  }: {
                    title: string
                    description: string
                  }) => ({
                    questionName: title,
                    acceptedAnswerText: description
                  })
                )
        }
      />
      {/* ItemList = THIS page's inventory only (the search can pad with
          other cities/pillars), else the directory rows a zero-supply page
          renders instead. Never emit an empty list. No HowTo: Google retired
          HowTo rich results (2026-09-30). */}
      {itemListUrls.length > 0 && (
        <CarouselJsonLd
          ofType="default"
          data={itemListUrls.map((path) => ({
            url: `https://www.spotme.mx${path}`
          }))}
        />
      )}

      <main className="h-full">
        <LostListingToast />
        <FilterProvider initialFilters={initialFilters}>
          <SearchManager
            initialData={initialData}
            header={header}
            seoFooter={seoFooter}
            nearbySearchData={nearbySearchData ?? undefined}
            initialDirectory={initialDirectory ?? undefined}
            locationName={hoodName ?? cityNameShort ?? cityName ?? undefined}
            priorityImages
          />
        </FilterProvider>

        <GooglePopup />
      </main>
    </>
  )
}

export default Page

export type IParams = {
  pillar: PillarPage
  city?: SearchPageCity
  hood?: SearchHood
}

/**
 * ISR conversion (2026-08-31) — the third architecture, and why it's safe.
 *
 * History, because each step had a reason:
 *  - Originally `getStaticProps` + a curated prerender list. Killed April 2026
 *    because `?sizes=` deep links silently rendered the unfiltered list.
 *  - April 2026 → `getServerSideProps` reading filters from `query`, with
 *    `s-maxage` + SWR edge caching. Correct, but the CDN keys on the FULL
 *    request URL: every unique `gclid` was its own cache entry, so every paid
 *    click MISSed and paid a 292–1,049 ms SSR render before the first byte —
 *    measured live 2026-08-31 on /renta-mini-bodegas/monterrey: bare URL HIT
 *    121 ms, same URL + gclid MISS 1,037–1,583 ms, byte-identical HTML, on the
 *    page where ~91% of mobile traffic is paid. A rewriting proxy was tried
 *    (2026-08-27) and does NOT work — the key is the ORIGINAL URL. Don't
 *    re-propose one; see docs/performance/large-page-data.md.
 *  - Now: back to `getStaticProps` + `revalidate` + `fallback: 'blocking'`.
 *    The prerender cache ignores the query string entirely (same reason
 *    `[configId]` never had this problem), so gclid/utm/hsa_* variants all
 *    serve the same cached page instantly.
 *
 * What makes the April 2026 bug NOT come back this time:
 *  1. `FilterProvider` merges `sizes`/`minPrice`/`maxPrice`/`sortBy` from
 *     `router.query` OVER the baked `initialFilters` (its own April fix,
 *     client-side — it never depended on SSR).
 *  2. `SearchManager` now discards `initialData` when the landing URL carries
 *     any of those overrides (see `FILTER_OVERRIDE_PARAMS` there), running its
 *     normal /search fetch path instead: default list → skeleton → filtered
 *     results. Measured 30d before the conversion: ~17% of pillar landings
 *     carry an override (~1.4k organic-Google + ~0.7k paid `?sizes=` / month)
 *     and pay that brief reshuffle; the other 83% — and 100% of gclid-tagged
 *     paid clicks — go from a guaranteed ~1s MISS to an instant CDN hit.
 *  3. Everything INTERACTIVE already leaves this page: `updateParams` pushes
 *     to /search on any filter change or map pan, so the static snapshot only
 *     ever has to serve its own URL.
 *
 * `revalidate: 21600` mirrors the SSR era's unfiltered `s-maxage=21600`, and
 * ISR's serve-stale-then-regenerate is strictly stronger than the 2026-08-27
 * SWR widening it replaces (no visitor ever waits on a revalidation, and a
 * revalidation that THROWS keeps serving the last good page — which also
 * preserves the "backend blip must not 404/deindex these pages" guarantee the
 * SSR error path enforced with a 5xx).
 *
 * NOT converted: `/[pillar]` (pages/[pillar]/index.tsx). Its SSR reads
 * `?city=` to bake the personalized hero server-side for paid landings —
 * a live April-2026 reason ISR would regress. Paid traffic now lands on
 * city pages directly (2026-08-29 lander move), so that page's miss tax
 * matters far less.
 */

// Cap every backend call in this SSR path. Search p99 is a few seconds; a
// dyno restart makes the Heroku router accept the socket but never respond,
// so without a deadline the fetch neither resolves nor rejects and the Vercel
// function hangs to its 60s ceiling (billed + a hard 504). 10s is generous
// headroom over a cold OpenSearch query while failing 6× faster than the cap.
const SSR_QUERY_TIMEOUT_MS = 10_000
/**
 * Directory rows baked into a zero-supply city page. Raised 12 → 24 with the
 * city-guide pass (2026-10-20): the biggest no-supply metros hold 20–22
 * facilities (Tijuana 21), and a guide that silently stops at 12 hides real
 * options. The payload stays small — a directory row carries no photos,
 * configurations or pricing objects.
 */
const DIRECTORY_ZERO_SUPPLY_ROWS = 24

/**
 * Nothing is prerendered at the hood level — every hood URL renders on its
 * first hit (`fallback: 'blocking'`, the same cost as one of the old SSR
 * cache MISSes) and is then served from the prerender cache until
 * `revalidate` elapses. The money pages are prerendered at BUILD time by the
 * city-level `getStaticPaths` in `../index.tsx`, so a deploy — which wipes
 * the ISR cache — never leaves the paid landers cold.
 */
export const getStaticPaths = (async () => {
  return { paths: [], fallback: 'blocking' }
}) satisfies GetStaticPaths

export const getStaticProps: GetStaticProps<IProps | {}> = async (context) => {
  const { params, locale = 'es' } = context
  const { pillar, city, hood } = params as IParams

  if (
    city?.endsWith('.php') ||
    city?.endsWith('.js') ||
    hood?.endsWith('.php') ||
    hood?.endsWith('.js')
  ) {
    return { notFound: true }
  }

  if (!Object.values(PillarPage).includes(pillar)) {
    return { notFound: true }
  } else if (city && !Object.values(SearchPageCity).includes(city)) {
    return { notFound: true }
  } else if (hood && !(hood in HOOD_MAP)) {
    return { notFound: true }
  } else if (
    hood &&
    city &&
    !SEARCH_PAGE_CITY_MAP[city]?.hoods?.includes(hood)
  ) {
    // A CDMX hood under another city rendered with CDMX coordinates and a
    // self-canonical (`/pension-estacionamiento/monterrey/centro` was earning
    // GSC impressions). Send it to the real city page.
    return {
      redirect: { destination: `/${pillar}/${city}`, permanent: true }
    }
  }

  const {
    searchName,
    label,
    query: pillarQuery,
    keywords
  } = PILLAR_PAGE_MAP[pillar] ?? {}
  const { cityName, placeId, cityNameShort } = city
    ? (SEARCH_PAGE_CITY_MAP[city] ?? {})
    : {
        cityName: null,
        placeId: SEARCH_PAGE_CITY_MAP[SearchPageCity.CiudadDeMexico].placeId,
        cityNameShort: null
      }
  const { hoodName, placeId: placeIdHood } = hood
    ? (HOOD_MAP[hood] ?? {})
    : { hoodName: null, placeId: null }

  if (!searchName || !label || !pillarQuery || !keywords) {
    return { notFound: true }
  }

  // No `query` here — getStaticProps cannot see it, which is exactly the
  // point (the prerender cache ignores it). URL filter overrides
  // (`?sizes=` …) are honored client-side: FilterProvider merges them over
  // these baked defaults, and SearchManager discards `initialData` when the
  // landing URL carries one. See the ISR-conversion comment above.
  const initialFilters = {
    ...pillarQuery,
    staticPage: true,
    zoom:
      city === SearchPageCity.CiudadDeMexico
        ? hood
          ? 14
          : 11
        : hood
          ? 13
          : 11,
    placeId: placeIdHood ?? placeId,
    description: hoodName ?? cityNameShort ?? cityName,
    search: hoodName ?? cityNameShort ?? cityName
  } as Partial<IQueryParams>

  const effectivePlaceId = initialFilters.placeId ?? ''
  const cachedCoords = effectivePlaceId
    ? PLACE_ID_TO_COORDS[effectivePlaceId]
    : undefined

  const client = getApolloClient(FAKE_CTX)

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  const { description, staticPage, spaceTypes, search, ...searchInput } =
    initialFilters

  const SPACE_TYPE_TO_LOCATION_TYPE: Partial<Record<SpaceType, string>> = {
    [SpaceType.SELF_STORAGE]: 'self-storage',
    [SpaceType.PARKING]: 'parking',
    [SpaceType.WAREHOUSE]: 'warehouse',
    [SpaceType.INDUSTRIAL_WAREHOUSE]: 'industrial-warehouse'
  }
  const primarySpaceType = pillarQuery.spaceTypes?.[0] as SpaceType | undefined
  const locationType = primarySpaceType
    ? SPACE_TYPE_TO_LOCATION_TYPE[primarySpaceType]
    : undefined

  // Run search query + translations in parallel (+ nearby counts when coords are cached)
  const searchPromise = client.query<
    Pick<IQuery, 'searchLocations' | 'googlePlaceDetails'>,
    IQuerySearchLocationsArgs &
      IQueryGooglePlaceDetailsArgs & { fetchPlace: boolean }
  >({
    query: SEARCH_LOCATIONS,
    variables: {
      pagination: { from: 0, pageSize: SSG_PAGE_SIZE },
      searchInput: {
        ...searchInput,
        center: cachedCoords ?? undefined
      },
      placeId: effectivePlaceId,
      fetchPlace: !cachedCoords
    },
    context: {
      fetchOptions: { signal: AbortSignal.timeout(SSR_QUERY_TIMEOUT_MS) }
    }
  })

  const translationsPromise = loadTranslations(locale, [
    PILLAR_NS_MAP[pillar],
    'common',
    'footer',
    'nav',
    'host_cta',
    'auth',
    'platform_stats'
    // `page_mini_bodega` intentionally NOT loaded here — the OnDemand cards
    // that use it (<OnDemandSearchCard> / <OnDemandRecoveryCard>) render only
    // on SelfStorage, where `PILLAR_NS_MAP[pillar]` already IS
    // `page_mini_bodega`; the cards also lazy-load it via useLazyNamespace as a
    // safety net. Dropping it saves ~37 kB on every non-storage pillar search
    // page. See docs/performance/large-page-data.md.
  ])

  // When coords are cached, fire nearby counts in parallel with the search query
  const nearbyPromise =
    cachedCoords && locationType
      ? client
          .query<NearbySearchCountsData, NearbySearchCountsVars>({
            query: NEARBY_SEARCH_COUNTS,
            variables: {
              input: {
                lat: cachedCoords.lat,
                lng: cachedCoords.lng,
                locationType
              }
            },
            context: {
              fetchOptions: {
                signal: AbortSignal.timeout(SSR_QUERY_TIMEOUT_MS)
              }
            }
          })
          .then((r) => r.data?.nearbySearchCounts)
          .catch(() => null)
      : null

  let response
  let translations: Awaited<ReturnType<typeof serverSideTranslations>>
  try {
    ;[response, translations] = await Promise.all([
      searchPromise,
      translationsPromise
    ])
  } catch (error) {
    // Reaching here means the search/place call threw or timed out — an infra
    // failure, NOT an invalid slug (pillar/city/hood validity is already
    // enforced by the guards above; a bad hood-in-city just returns 0 hits, it
    // doesn't throw). Serving a 404 here would tell Google to deindex these
    // pillar pages on a transient backend blip. Re-throw instead: on a
    // first-ever (`fallback: 'blocking'`) render that's an uncached 5xx
    // (search engines treat 5xx as retry-later), and on a background
    // revalidation ISR keeps serving the last GOOD page — strictly better
    // than the SSR era's no-store + 5xx on every request during an outage.
    processRequestError(error, 'Error retrieving locations server-side search')
    throw error
  }

  const { lat, lng } = cachedCoords ??
    response.data?.googlePlaceDetails ?? { lat: 0, lng: 0 }

  // If coords weren't cached, fetch nearby counts now (sequential fallback)
  let nearbySearchData: NearbySearchCountsResult | null =
    (await nearbyPromise) ?? null
  if (!nearbySearchData && !cachedCoords && lat && lng) {
    try {
      const { data } = await client.query<
        NearbySearchCountsData,
        NearbySearchCountsVars
      >({
        query: NEARBY_SEARCH_COUNTS,
        variables: {
          input: { lat, lng, ...(locationType ? { locationType } : {}) }
        },
        context: {
          fetchOptions: { signal: AbortSignal.timeout(SSR_QUERY_TIMEOUT_MS) }
        }
      })
      nearbySearchData = data?.nearbySearchCounts ?? null
    } catch {
      // Non-critical — page renders fine without CTA data
    }
  }

  const initialData =
    cachedCoords && !response.data?.googlePlaceDetails
      ? {
          ...response,
          data: {
            ...response.data,
            googlePlaceDetails: {
              placeId: effectivePlaceId,
              lat: cachedCoords.lat,
              lng: cachedCoords.lng
            }
          }
        }
      : response

  // ZERO-SUPPLY city page (2026-08-28, Jules: "be useful for free where we
  // have nothing"): when the search is empty, fetch the top directory rows
  // here so `DirectoryZeroSupply` renders them INTO the HTML — the lazy floor
  // is `ssr: false` and a crawler (or a renter on a slow phone) otherwise
  // meets an empty page in ~60 metros where the directory is all we have.
  // Best-effort: any failure leaves the page exactly as before.
  //
  // 2026-10-20: also on WEAK-supply mini-bodega pages outside Monterrey
  // (`directoryGuideMode` — CDMX 11 listings vs 99 facilities, GDL 6 vs 23),
  // where the guide renders right AFTER the SpotMe listings instead of the
  // muted lazy floor. SearchList tells the two modes apart by whether the
  // ranked results are empty.
  const directoryCategory = directoryCategoryForPillar(pillar)
  const searchTotal = response.data?.searchLocations?.total ?? 0
  const guideMode = directoryGuideMode({
    pillar,
    searchTotal,
    center: lat && lng ? { lat, lng } : null
  })
  let initialDirectory: ILocation[] | null = null
  if (guideMode && directoryCategory && lat && lng) {
    initialDirectory = await client
      .query<Pick<IQuery, 'directoryLocations'>, IQueryDirectoryLocationsArgs>({
        query: DIRECTORY_GUIDE_LOCATIONS,
        variables: {
          pagination: { from: 0, pageSize: DIRECTORY_ZERO_SUPPLY_ROWS },
          searchInput: {
            center: { lat, lng },
            zoom: initialFilters.zoom,
            locationTypes: DIRECTORY_CATEGORY_LOCATION_TYPES[directoryCategory]
          }
        },
        context: {
          fetchOptions: { signal: AbortSignal.timeout(SSR_QUERY_TIMEOUT_MS) }
        }
      })
      .then((r) => (r.data?.directoryLocations?.hits ?? []) as ILocation[])
      .catch(() => null)
    if (initialDirectory && initialDirectory.length > 0) {
      // The `directorio` namespace is lazy everywhere else (it only serves a
      // gated surface); here the cards are in the SSR HTML, so merge it in —
      // only on this branch, never bloating a page that has results.
      const extra = await serverSideTranslations(locale, ['directorio'])
      const base = translations._nextI18Next
      const add = extra._nextI18Next
      if (base && add) {
        for (const lng of Object.keys(add.initialI18nStore ?? {})) {
          base.initialI18nStore[lng] = {
            ...(base.initialI18nStore[lng] ?? {}),
            ...add.initialI18nStore[lng]
          }
        }
        base.ns = [...new Set([...(base.ns ?? []), ...(add.ns ?? [])])]
      }
    } else {
      initialDirectory = null
    }
  }

  return {
    props: {
      pillar,
      city: city ?? null,
      hood: hood ?? null,
      label,
      initialFilters: { ...initialFilters, lat, lng },
      ...translations,
      initialData,
      searchName,
      cityName,
      hoodName,
      cityNameShort: cityNameShort ?? null,
      locale,
      keywords,
      nearbySearchData,
      initialDirectory,
      // Revalidate-time date (feeds `lastReviewed` JSON-LD) — under ISR this
      // refreshes with each regeneration, same freshness as the page data.
      buildDate: new Date().toISOString().split('T')[0]
    },
    // Mirrors the SSR era's unfiltered `s-maxage=21600` (6h). ISR serves
    // stale and regenerates in the background, so no visitor ever waits on
    // this — see the conversion comment above.
    revalidate: 21600
  }
}
